// Outcome options for a door-to-door registration, from the deuractie concept doc.
// The detail (sub-)options depend on the chosen outcome.
//
// AI-CONTEXT: See docs/features/wervingsacties-registratie.md (data model)

import { EIGENDOM_OPTIONS } from '../adressHelpers/householdFields'
import buildFixedFieldPatch from '../adressHelpers/buildFixedFieldPatch'

const UITKOMST_OPTIONS = [
    { value: 'nee', label: 'Nee' },
    { value: 'misschien', label: 'Doet misschien mee' },
    { value: 'ja', label: 'Ja' },
]

const UITKOMST_DETAIL_OPTIONS = {
    nee: [
        { value: 'klaar', label: 'Klaar' },
        { value: 'fixt-zelf', label: 'Fixt zelf' },
        { value: 'anders', label: 'Anders' },
    ],
    misschien: [
        { value: 'terugkomen', label: 'Terugkomen' },
        { value: 'meldt-zichzelf-aan', label: 'Meldt zichzelf aan' },
    ],
    ja: [
        { value: 'meldt-zichzelf-aan', label: 'Meldt zichzelf aan' },
        { value: 'wij-melden-aan', label: 'Wij melden aan' },
        // Selecting this + Opslaan creates a household (Adres) — see Registratie.jsx.
        { value: 'automatisch-aanmelden', label: 'Automatisch aanmelden (maak huishouden aan)' },
    ],
}

// Aan de deur dezelfde keuzes als 'Type woning' op het huishouden, met dezelfde
// sleutels: dan krijgt een automatisch aangemaakt huishouden meteen een
// beslisbaar eigendom (besluit user 2026-10-28). Een registratie van vóór die
// dag zegt alleen 'huur'; die blijft leesbaar als 'Huurwoning'.
// AI-CONTEXT: See docs/features/huishouden-vaste-gegevens.md (T11)
const WONINGTYPE_OPTIONS = EIGENDOM_OPTIONS
const LEGACY_HUUR = { value: 'huur', label: 'Huurwoning' }

/** @param {string} value @returns {boolean} Of dit een huurwoning is, welke soort ook */
const isHuur = (value) => value === LEGACY_HUUR.value || String(value || '').startsWith('huur-')

/** @param {string} value @returns {string} Het label, of '—' als er niets gekozen is */
const woningtypeLabel = (value) => (value === LEGACY_HUUR.value
    ? LEGACY_HUUR.label
    : WONINGTYPE_OPTIONS.find((o) => o.value === value)?.label || '—')

/**
 * De keuzes voor het formulier. Staat er nog een oude 'huur', dan blijft die
 * kiesbaar: anders toont het menu stil een andere keuze dan wat er bewaard is.
 * @param {string} current
 * @returns {Array<{value: string, label: string}>}
 */
const woningtypeOptionsFor = (current) => (current === LEGACY_HUUR.value
    ? [LEGACY_HUUR, ...WONINGTYPE_OPTIONS]
    : [...WONINGTYPE_OPTIONS])

/**
 * De vaste gegevens van een huishouden dat uit deze registratie wordt
 * aangemaakt. Alleen wat klopt: een ongeldig e-mailadres of een oude 'huur'
 * (corporatie of particulier is dan onbekend) blijft weg, dan vult de planner
 * het aan op de kaart 'Vaste gegevens'. Elk veld via dezelfde validatie als die
 * kaart (`buildFixedFieldPatch`).
 * @param {object} reg - De registratie (adres uit het register)
 * @param {object} form - Wat aan de deur is ingevuld
 * @returns {Record<string, string>}
 */
const buildRegistratieHousehold = (reg, form) => {
    const bronnen = {
        Postcode: reg?.Postcode,
        Huisnummer: reg?.Huisnummer,
        Toevoeging: reg?.HuisnummerToevoeging,
        Straatnaam: reg?.Straatnaam,
        Woonplaats: reg?.Woonplaats,
        Telefoon: form?.Telefoon,
        Email: form?.Email,
        Eigendom: form?.Woningtype,
    }
    return Object.entries(bronnen).reduce((velden, [field, raw]) => {
        const { patch } = buildFixedFieldPatch(field, raw ?? '')
        const value = patch ? patch[field] : ''
        return value ? { ...velden, [field]: value } : velden
    }, {})
}

// Detail value that triggers auto-creating a household (Adres) on save.
const AUTO_REGISTER_DETAIL = 'automatisch-aanmelden'

const detailOptionsFor = (uitkomst) => UITKOMST_DETAIL_OPTIONS[uitkomst] || []

// De velden die aan de deur worden ingevuld, met hun lege waarde. Eén bron
// voor het formulier (RegistratieModal), het opslaan (Registratie), de lege
// rij bij aanvinken (writeSelection) en de vraag 'staat hier al iets in?'
// (selectionPlan). Die vier hadden elk een eigen lijst, en een veld erbij in
// het formulier liet loslaten zonder bevestiging gebeuren.
// AI-CONTEXT: See docs/pitfalls.md#dev-mirrored-constant-guarded-only-by-a-comment
const EMPTY_REGISTRATIE = Object.freeze({
    // De datum van het deurgesprek. Heette eerst 'Datum werfactie'; bestaande
    // waarden lezen als gespreksdatum, zonder migratie (besluit Gijs 2026-10-12).
    Datum: '',
    DatumBriefGepost: '',
    BriefGepost: false,
    Deurgesprek: false,
    Uitkomst: '',
    UitkomstDetail: '',
    Woningtype: '',
    Email: '',
    Telefoon: '',
    Opmerkingen: '',
    // Sleutels uit CompagnyMeta.WervingsDiensten (resolveWervingsDiensten), alleen bij 'ja'.
    Diensten: Object.freeze([]),
})

// Een dienst hoort bij iemand die meedoet: bij elke andere uitkomst gaat de lijst leeg.
const UITKOMST_MET_DIENSTEN = 'ja'

/**
 * Het formulier voor een registratierij: ja/nee-velden als boolean, lijsten als
 * een eigen array, de rest als tekst.
 *
 * @param {object | null} registratie - Wervingsregistratie, of null voor een leeg formulier
 * @returns {object}
 */
const registratieFormFrom = (registratie) => Object.fromEntries(
    Object.entries(EMPTY_REGISTRATIE).map(([veld, leeg]) => {
        const waarde = registratie?.[veld]
        if (typeof leeg === 'boolean') return [veld, waarde === true || waarde === 'true']
        // Altijd een eigen lijst: het formulier mag de rij of de lege vorm niet raken.
        if (Array.isArray(leeg)) return [veld, Array.isArray(waarde) ? waarde.filter((k) => typeof k === 'string') : []]
        return [veld, waarde || leeg]
    }),
)

/**
 * Wat er bij opslaan naar de rij gaat: alleen de registratievelden uit het
 * formulier, en de diensten alleen bij een 'ja'.
 *
 * @param {object} form
 * @returns {object}
 */
const registratieUpdate = (form) => ({
    ...Object.fromEntries(Object.keys(EMPTY_REGISTRATIE).map((veld) => [veld, form[veld]])),
    Diensten: form.Uitkomst === UITKOMST_MET_DIENSTEN && Array.isArray(form.Diensten) ? form.Diensten : [],
})

export {
    UITKOMST_OPTIONS,
    UITKOMST_DETAIL_OPTIONS,
    WONINGTYPE_OPTIONS,
    isHuur,
    woningtypeLabel,
    woningtypeOptionsFor,
    buildRegistratieHousehold,
    AUTO_REGISTER_DETAIL,
    EMPTY_REGISTRATIE,
    UITKOMST_MET_DIENSTEN,
    detailOptionsFor,
    registratieFormFrom,
    registratieUpdate,
}
